import React, {
    useRef,
    useEffect,
    useState,
    useLayoutEffect,
    useCallback
} from 'react';

import PropTypes from 'prop-types';

const Expandable = ({isExpanded, animateHeightChange, children, animationDuration = 300}) => {
    const internalRef = useRef(null);
    const animationTimeout = useRef(null);
    const resizeObserver = useRef(null);

    const [rendered, setRendered] = useState(isExpanded);
    const [didInit, setDidInit] = useState(!isExpanded);
    const [isVisible, setIsVisible] = useState(isExpanded);
    const [limitOverflow, setLimitOverflow] = useState(animateHeightChange);
    const [versionKey, setVersionKey] = useState(0);

    const calculatedHeight = useCallback(() => {
        if (!internalRef.current) {
            return 0;
        }

        return internalRef.current.getBoundingClientRect().height || 0;
    }, []);

    const maxHeight = () => {
        if (!isVisible) return '0px';
        if (!limitOverflow) return 'initial';
        return `${calculatedHeight()}px`;
    };

    const cancelAnimation = () => {
        setLimitOverflow(animateHeightChange);
        if (animationTimeout.current) {
            clearTimeout(animationTimeout.current);
            animationTimeout.current = null;
        }
    };

    const toggleResizeObserver = useCallback(() => {
        if (resizeObserver.current) {
            resizeObserver.current.disconnect();
            resizeObserver.current = null;
        }


        if (animateHeightChange && rendered && internalRef.current) {
            resizeObserver.current = new ResizeObserver(() => {
                setVersionKey(prev => (prev < 100 ? prev + 1 : 1));
            });
            resizeObserver.current.observe(internalRef.current);
        }
    }, [animateHeightChange, rendered]);

    const toggle = (newVal) => {
        if (!newVal) {
            animationTimeout.current = setTimeout(() => {
                animationTimeout.current = null;
                setRendered(false);
                setLimitOverflow(animateHeightChange);
                toggleResizeObserver(); // reset
            }, animationDuration);
        } else {
            setRendered(true);
            animationTimeout.current = setTimeout(() => {
                animationTimeout.current = null;
                setLimitOverflow(animateHeightChange);
                toggleResizeObserver();
            }, animationDuration);
        }

        requestAnimationFrame(() => setIsVisible(newVal));
    };

    // cancelAnimation/toggle are non-memoized closures; only re-run on isExpanded change
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        cancelAnimation();
        setDidInit(true);
        setLimitOverflow(true);
        setTimeout(() => toggle(isExpanded));
    }, [isExpanded]);

    // toggleResizeObserver is invoked directly from toggle() when `rendered` flips; re-running here on its identity change would be redundant
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        toggleResizeObserver();
        return () => {
            if (resizeObserver.current && resizeObserver.current.disconnect) {
                resizeObserver.current.disconnect();
            }
        };
    }, [limitOverflow, animateHeightChange]);

    useLayoutEffect(() => {
        setTimeout(() => setDidInit(true), 0);
    }, []);

    const contentStyle = {
        opacity: isVisible ? 1 : 0,
        overflow: limitOverflow ? 'hidden' : 'initial',
        transition: didInit ? `height ${animationDuration}ms ease-in-out, opacity ${animationDuration}ms ease-in-out` : 'none',
        height: maxHeight(),
        content: versionKey // just to trigger rerender on height change
    };

    if (!rendered) return null;

    return (
        <div className="evc-expandable-component relative" style={contentStyle}>
            <div ref={internalRef}>
                {children}
            </div>
        </div>
    );
};

Expandable.propTypes = {
    isExpanded: PropTypes.bool,
    animateHeightChange: PropTypes.bool,
    animationDuration: PropTypes.number,
    children: PropTypes.node.isRequired,
};

Expandable.defaultProps = {
    isExpanded: false,
    animateHeightChange: false,
    animationDuration: 300,
};

export default Expandable;
